import React, { useMemo } from 'react';
import { Box, Paper, Typography, Popover, Button, Chip } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { isViewOnly } from '../utils/permissions';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import StraightenIcon from '@mui/icons-material/Straighten';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import * as geojson from 'geojson';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useUnitSystem } from '../hooks/useUnitSystem';
import { formatArea } from '../utils/units';
import { useTimezone } from "../contexts/TimezoneContext";
import { formatInTimezone, parseInTimezone } from "../utils/datetime";

interface FireHistoryLayerPopoverProps {
  feature: geojson.Feature | null;
  anchorPosition: { top: number; left: number } | null;
  onClose: () => void;
  onUpdateDate?: (field: 'StartDate' | 'EndDate', value: string | null) => void;
  onSaveChanges?: () => void;
  onCancelPending?: () => void;
  hasPendingChanges?: boolean;
  isSaving?: boolean;
  draftEndDate?: string | null;
}

// Helper function to check if a value exists and is not undefined
const hasValue = (value: any): boolean => {
  return value !== undefined && value !== null && value !== '';
};

// Helper function to format date
const formatDate = (dateString: string | undefined, tz: string): string => {
  if (!dateString) return '';
  return formatInTimezone(dateString, tz, 'DD/MM/YYYY');
};

const FireHistoryLayerPopover: React.FC<FireHistoryLayerPopoverProps> = ({
  feature,
  anchorPosition,
  onClose,
  onUpdateDate,
  onSaveChanges,
  onCancelPending,
  hasPendingChanges,
  isSaving,
  draftEndDate,
}) => {
  const unitSystem = useUnitSystem();
  const { selectedTimezone } = useTimezone();
  const { user } = useAuth();
  const open = Boolean(feature && anchorPosition);
  const canEditDates = Boolean(feature && onUpdateDate) && !isViewOnly(user);

  const endDateValue = useMemo(() => {
    const value =
      draftEndDate !== undefined && draftEndDate !== null
        ? draftEndDate
        : feature?.properties?.EndDate;
    return parseInTimezone(value ? String(value) : null, selectedTimezone);
  }, [draftEndDate, feature?.properties?.EndDate, selectedTimezone]);

  const handleDateChange = (
    field: 'StartDate' | 'EndDate',
    value: dayjs.Dayjs | null
  ) => {
    if (!onUpdateDate || !feature?.id) {
      return;
    }
    onUpdateDate(
      field,
      value ? value.tz(selectedTimezone, true).format('YYYY-MM-DD') : null
    );
  };

  const renderDateEditor = (
    field: 'StartDate' | 'EndDate',
    label: string,
    value: dayjs.Dayjs | null
  ) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 1,
      }}
    >
      <CalendarMonthIcon sx={{ fontSize: 18 }} />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label={label}
          value={value}
          format="DD/MM/YYYY"
          disabled={isSaving}
          onChange={(newValue) => handleDateChange(field, newValue)}
          slotProps={{
            textField: {
              variant: 'standard',
              sx: {
                input: { color: 'white' },
                label: { color: 'rgba(255,255,255,0.7)' },
                '& .MuiInputBase-root:before': {
                  borderBottomColor: 'rgba(255,255,255,0.4)',
                },
                '& .MuiInputBase-root:after': {
                  borderBottomColor: '#00d4ad',
                },
              },
            },
          }}
        />
      </LocalizationProvider>
    </Box>
  );

  if (!feature) return null;

  return (
    <Popover
      open={open}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition || undefined}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
    >
      <Paper sx={{ p: 2, maxWidth: 300, bgcolor: '#212b37', color: 'white' }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
          <LocalFireDepartmentIcon sx={{ mr: 1 }} />
          {feature.properties?.FireName || 'Historical Fire'}
        </Typography>
        
        {canEditDates ? (
          <>
            {renderDateEditor('EndDate', 'End Date', endDateValue)}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
                mt: 1,
              }}
            >
              {hasPendingChanges && (
                <Button
                  variant="text"
                  color="inherit"
                  size="small"
                  onClick={onCancelPending}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
              )}
              <Button
                variant="contained"
                size="small"
                onClick={onSaveChanges}
                disabled={!hasPendingChanges || isSaving}
              >
                {isSaving ? 'Saving…' : 'Save'}
              </Button>
            </Box>
          </>
        ) : (
          <>
            {feature.properties?.DateMissing ? (
              <Chip
                icon={<CalendarMonthIcon />}
                label="Date unknown"
                size="small"
                sx={{
                  mb: 1,
                  bgcolor: 'rgba(255,255,255,0.12)',
                  color: 'rgba(255,255,255,0.7)',
                  fontStyle: 'italic',
                  '& .MuiChip-icon': { color: 'rgba(255,255,255,0.5)', fontSize: 16 },
                }}
              />
            ) : (
              <>
                {hasValue(feature.properties?.StartDate) && (
                  <Typography variant="body2" sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
                    <CalendarMonthIcon sx={{ mr: 1, fontSize: 18 }} />
                    Start Date: {formatDate(feature.properties?.StartDate, selectedTimezone)}
                  </Typography>
                )}
                {hasValue(feature.properties?.EndDate) && (
                  <Typography variant="body2" sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
                    <CalendarMonthIcon sx={{ mr: 1, fontSize: 18 }} />
                    End Date: {formatDate(feature.properties?.EndDate, selectedTimezone)}
                  </Typography>
                )}
              </>
            )}
          </>
        )}
        
        {hasValue(feature.properties?.Area) && (
          <Typography variant="body2" sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
            <StraightenIcon sx={{ mr: 1, fontSize: 18 }} />
            Area: {formatArea(Number(feature.properties?.Area), unitSystem)}
          </Typography>
        )}
        
        {hasValue(feature.properties?.Intensity) && (
          <Typography variant="body2" sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
            <WhatshotIcon sx={{ mr: 1, fontSize: 18 }} />
            Intensity: {feature.properties?.Intensity}
          </Typography>
        )}
        
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mt: 1, 
            pt: 1, 
            borderTop: '1px solid rgba(255,255,255,0.1)' 
          }}
        >
          <Box 
            component="span" 
            sx={{ 
              width: 18, 
              height: 18, 
              borderRadius: '50%', 
              bgcolor: feature.properties?.color || '#ffffff',
              mr: 1
            }} 
          />
          <Typography variant="body2">
            {(() => {
              if (feature.properties?.DateMissing) {
                return <span style={{ fontStyle: 'italic', opacity: 0.7 }}>Year: Unknown</span>;
              }
              const explicitYear = feature.properties?.year;
              if (explicitYear !== undefined && explicitYear !== null && explicitYear !== '') {
                return <>Year: {explicitYear}</>;
              }
              const parsed = parseInTimezone(
                feature.properties?.EndDate ? String(feature.properties?.EndDate) : null,
                selectedTimezone
              );
              return <>Year: {parsed ? parsed.year() : ''}</>;
            })()}
          </Typography>
        </Box>
      </Paper>
    </Popover>
  );
};

export default FireHistoryLayerPopover;
